/**
 * Pipedrive Note Detail Page
 * Shows full note content with related records
 */

import { useParams, Link } from 'react-router-dom'
import { usePipedriveNote } from '@/hooks/use-pipedrive-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  StickyNote,
  ArrowLeft,
  User,
  Building2,
  Briefcase,
  Calendar,
  ExternalLink,
  Pin,
} from 'lucide-react'

export default function PipedriveNoteDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: note, isLoading, error } = usePipedriveNote(id ?? '')

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg" role="alert">
          Failed to load note. Please try again later.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-32" />
      </div>
    )
  }

  if (!note) {
    return (
      <div className="p-6">
        <div className="bg-muted p-4 rounded-lg">Note not found.</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/pipedrive/notes">
          <Button variant="ghost" size="icon" aria-label="Back to notes">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <StickyNote className="h-6 w-6" aria-hidden="true" />
              Note
            </h1>
            {note.pinned_to_deal && (
              <Badge variant="secondary" className="gap-1">
                <Pin className="h-3 w-3" aria-hidden="true" />
                Pinned to Deal
              </Badge>
            )}
            {note.pinned_to_org && (
              <Badge variant="secondary" className="gap-1">
                <Pin className="h-3 w-3" aria-hidden="true" />
                Pinned to Org
              </Badge>
            )}
            {note.pinned_to_person && (
              <Badge variant="secondary" className="gap-1">
                <Pin className="h-3 w-3" aria-hidden="true" />
                Pinned to Person
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {note.user_name ? `By ${note.user_name}` : ''}
            {note.user_name && note.add_time ? ' • ' : ''}
            {note.add_time ? `Created: ${note.add_time}` : ''}
          </p>
        </div>
      </div>

      {/* Note Content */}
      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
        </CardHeader>
        <CardContent>
          {note.content ? (
            <div
              className="prose prose-sm max-w-none text-foreground"
              dangerouslySetInnerHTML={{ __html: note.content }}
            />
          ) : (
            <p className="text-muted-foreground">No content</p>
          )}
        </CardContent>
      </Card>

      {/* Details & Related Records */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {note.user_name && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Author:</span>
                <span className="font-semibold">{note.user_name}</span>
              </div>
            )}
            {note.add_time && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Created:</span>
                <span className="font-semibold">{note.add_time}</span>
              </div>
            )}
            {note.update_time && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Updated:</span>
                <span className="font-semibold">{note.update_time}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Related Records</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {note.organization_id ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Organization:</span>
                <Link
                  to={`/pipedrive/organizations/${note.organization_id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {note.organization || 'View Organization'}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : note.organization ? (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Organization:</span>
                <span>{note.organization}</span>
              </div>
            ) : null}

            {note.deal_id ? (
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Deal:</span>
                <Link
                  to={`/pipedrive/deals/${note.deal_id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {note.deal_title || 'View Deal'}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : note.deal_title ? (
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Deal:</span>
                <span>{note.deal_title}</span>
              </div>
            ) : null}

            {note.person_id ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Contact:</span>
                <Link
                  to={`/pipedrive/people/${note.person_id}`}
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {note.person || 'View Contact'}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            ) : note.person ? (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Contact:</span>
                <span>{note.person}</span>
              </div>
            ) : null}

            {!note.organization && !note.deal_title && !note.person && (
              <p className="text-muted-foreground text-sm">No related records</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
